import { mapActions, mapState, mapGetters } from 'vuex';
import purchaseOffer from '@/components/purchase/purchase-offer/purchase-offer.vue';
import purchaseOfferDesktop from '@/components/purchase/purchase-offer-desktop/purchase-offer-desktop.vue';
import purchaseYesNoSwitcher from '@/components/purchase/purchase-yes-no-switcher/purchase-yes-no-switcher.vue';
import purchaseReviewAndConfirm from '@/components/purchase/purchase-review-and-confirm/purchase-review-and-confirm.vue';
import loaderMixin from '@/mixins/loader';
import accountVerifyMixin from '@/mixins/accountVerify';
import notificationProxy from '@/services/notifications/proxy';
import { MODALS } from '@/components/app/app-modals/app-modals';
import { get } from 'lodash';

export default {
  mixins: [
    accountVerifyMixin,
    loaderMixin,
  ],
  components: {
    purchaseOffer,
    purchaseOfferDesktop,
    purchaseYesNoSwitcher,
    purchaseReviewAndConfirm,
  },
  props: {
    market: {},
    kind: {
      type: String,
      default: 'modal',
    },
    closeJourneyAfterSuccess: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    ...mapState('trade', ['tradeOptions', 'tradeSuccess']),
    ...mapGetters('app', ['isLayoutMobile']),
    contract() {
      if (!this.tradeOptions.contractId) return null;
      const contracts = this.market.contracts || this.market.marketContracts;
      return contracts.find(c => c.contractId === this.tradeOptions.contractId);
    },
    payload() {
      return {
        quantity: this.tradeOptions.quantity,
        pricePerShare: this.tradeOptions.pricePerShare,
        contractId: this.tradeOptions.contractId,
        tradeType: this.tradeType,
      };
    },
    tradeType() {
      switch (`${this.tradeOptions.operationType}_${this.tradeOptions.operationValue}`) {
        case 'buy_no': return 0;
        case 'buy_yes': return 1;
        case 'sell_no': return 2;
        case 'sell_yes': return 3;
        default: return -1;
      }
    },
  },
  data() {
    return {
      hasInitialised: false,
      confirmationData: null,
      confirmationDoneData: null,
    };
  },
  watch: {
    tradeOptions: {
      handler: 'tradeOptionsHandler',
      immediate: true,
    },
    tradeSuccess(value) {
      if (value) {
        this.setCurrentPopup();
        this.$emit('reinitialize');
        // desktop exclusive
        if (this.closeJourneyAfterSuccess) {
          this.setTradeOptions(null);
        }
        this.setTradeSuccess(false);
      }
    },
    confirmationData() {
      this.$emit('confirmationDataChange', this.confirmationData);
    },
  },
  methods: {
    ...mapActions('profile', ['clearTradedMarkets']),
    ...mapActions('market', ['getContract', 'setMarketEngineBusyMessage']),
    ...mapActions('trade', [
      'submitTrade', 'confirmTrade', 'setTradeOptions', 'setTradeQuantity',
      'setTradeSuccess',
    ]),
    ...mapActions('notifications', ['addSimple']),
    ...mapActions('popup', ['setCurrentPopup', 'setCurrentPopupOptions']),
    getErrorFromResponse(response) {
      if (!response) return '';
      if (typeof response.data === 'string') {
        return response.data;
      }
      return (response.data) ? response.data.message : '';
    },
    tradeOptionsHandler(tradeOptions, oldOptions = {}) {
      if (oldOptions.contractId !== tradeOptions.contractId) {
        this.confirmationData = null;
        this.confirmationData = null;
      }
      if (
        oldOptions.operationValue !== tradeOptions.operationValue ||
        oldOptions.operationType !== tradeOptions.operationType
      ) {
        this.confirmationData = null;
        this.confirmationDoneData = null;
      }
    },
    onClickNext() {
      if (this.isLoading) {
        return;
      }

      if (!this.isUserVerified) {
        this.showAccountVerifyPopup();
        return;
      }

      // if (this.isUserBalanceEmpty) {
      //   this.showAddFundsPopup();
      //   return;
      // }

      if (this.tradeOptions.skipConfirmation) {
        this.submitTradeAction();
      } else {
        this.loaderAdd();
        this.confirmTrade(this.payload)
          .then((response) => {
            this.loaderRemove();
            this.confirmationDoneData = null;
            this.confirmationData = response.data;
            // when quantity is reduced it has to be set
            if (this.confirmationData.quantity) {
              this.setTradeQuantity(this.confirmationData.quantity);
            }
          })
          .catch((error) => {
            this.inspectErrorForRestrictions(error);
            this.loaderRemove();
            const message = this.getErrorFromResponse(error.response);
            if (message) {
              this.addSimple({
                icon: 'cancel',
                text: message,
              });
            }
          });
      }
    },
    submitTradeAction() {
      this.loaderAdd();
      this.submitTrade(this.payload)
        .then((response) => {
          const data = response.data;
          this.$gtm.trackEvent({ event: 'Trade' });
          this.loaderRemove();
          this.confirmationData = null;
          this.confirmationDoneData = data;
          this.addSimple({
            externalId: (data.offer) ? (data.offer.offerId) : null,
            closeTimeout: 3500,
            icon: 'pending',
            text: 'Offer received. You will be notified when processing is complete.',
          });
          this.setMarketEngineBusyMessage({
            isEngineBusy: data.isEngineBusy,
            isEngineBusyMessage: data.isEngineBusyMessage,
          });
          this.setTradeSuccess(true);
          this.clearTradedMarkets();
          const isBuyTradeType = [0, 1].includes(this.tradeType);
          notificationProxy.showMessagingJourney(isBuyTradeType);
        })
        .catch((error) => {
          this.inspectErrorForRestrictions(error);
          this.setTradeSuccess(false);
          this.loaderRemove();
          const message = this.getErrorFromResponse(error.response);
          if (message) {
            this.addSimple({
              icon: 'cancel',
              text: message,
            });
          }
        });
    },
    onClickSubmit() {
      this.submitTradeAction();
    },
    onClickAmend() {
      this.confirmationData = null;
      this.confirmationDoneData = null;
    },
    inspectErrorForRestrictions(error) {
      const restrictionPreamble = get(error, 'response.data.restrictionPreamble', null);
      const restrictions = get(error, 'response.data.restrictions', null);
      if (restrictionPreamble && restrictions) {
        this.setCurrentPopup(MODALS.MARKET_OWNERSHIP_CONFLICT);
        this.setCurrentPopupOptions({
          restrictions,
          restrictionPreamble,
        });
      }
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/purchase/purchase-journey/purchase-journey.js